import { Controller } from '@hotwired/stimulus'

/**
 * Count-up animation controller for stat values
 *
 * Usage:
 *   <div data-controller="count-up" data-count-up-duration-value="2000">
 *     <span data-count-up-target="value">10K+</span>
 *     <span data-count-up-target="value">99%</span>
 *     <span data-count-up-target="value">2,500</span>
 *   </div>
 *
 * The controller parses stat values like:
 * - "10K+" -> animates to 10000, displays as "10K+"
 * - "99%" -> animates to 99, displays as "99%"
 * - "2,500" -> animates to 2500, displays as "2,500"
 * - "24/7" -> doesn't animate (non-numeric)
 */
export default class extends Controller {
  static targets = ['value']
  static values = {
    duration: { type: Number, default: 2000 },
    threshold: { type: Number, default: 0.2 },
    once: { type: Boolean, default: true }
  }

  connect() {
    this.hasAnimated = false
    this.parsedValues = []

    // Parse all stat values
    this.valueTargets.forEach((el, index) => {
      const parsed = this.parseStatValue(el.textContent.trim())
      this.parsedValues[index] = parsed

      // Store original text for non-animatable values
      el.dataset.originalValue = el.textContent.trim()

      // Start at 0 for animatable values
      if (parsed.animatable) {
        el.textContent = this.formatValue(0, parsed)
      }
    })

    // Set up intersection observer
    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      {
        threshold: this.thresholdValue,
        rootMargin: '0px 0px -50px 0px'
      }
    )

    this.observer.observe(this.element)
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && (!this.onceValue || !this.hasAnimated)) {
        this.animate()
        this.hasAnimated = true

        if (this.onceValue) {
          this.observer.disconnect()
        }
      }
    })
  }

  animate() {
    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.showFinalValues()
      return
    }

    const startTime = performance.now()
    const duration = this.durationValue

    const tick = (currentTime) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Ease-out cubic
      const easeOut = 1 - Math.pow(1 - progress, 3)

      this.valueTargets.forEach((el, index) => {
        const parsed = this.parsedValues[index]
        if (parsed.animatable) {
          const current = parsed.value * easeOut
          el.textContent = this.formatValue(current, parsed)

          // Add counting class for CSS effects
          el.classList.add('is-counting')
        }
      })

      if (progress < 1) {
        requestAnimationFrame(tick)
      } else {
        // Ensure final values are exact
        this.showFinalValues()
      }
    }

    requestAnimationFrame(tick)
  }

  showFinalValues() {
    this.valueTargets.forEach((el, index) => {
      const parsed = this.parsedValues[index]
      if (parsed.animatable) {
        el.textContent = this.formatValue(parsed.value, parsed)
      } else {
        el.textContent = el.dataset.originalValue
      }
      el.classList.remove('is-counting')
      el.classList.add('count-complete')
    })
  }

  /**
   * Parse a stat value string into components
   * @param {string} text - The stat value text (e.g., "10K+", "99%", "2,500")
   * @returns {Object} - { value: number, prefix: string, suffix: string, animatable: boolean, format: string }
   */
  parseStatValue(text) {
    // Handle special non-numeric cases like "24/7"
    if (/^\d+\/\d+$/.test(text)) {
      return { value: 0, prefix: '', suffix: '', animatable: false, format: 'special', original: text }
    }

    // Match patterns like: "10K+", "$2.5M", "99%", "2,500+", "100+"
    const match = text.match(/^([^0-9]*)([0-9,.]+)\s*([KkMmBb]?)([^0-9KkMmBb]*)$/)

    if (!match) {
      return { value: 0, prefix: '', suffix: '', animatable: false, format: 'text', original: text }
    }

    const [, prefix, numStr, multiplier, suffix] = match

    // Parse the number (remove commas)
    let value = parseFloat(numStr.replace(/,/g, ''))

    // Handle K/M/B multipliers for display purposes
    let displayMultiplier = ''
    if (multiplier) {
      displayMultiplier = multiplier.toUpperCase()
      // Don't multiply the value - we'll format it with the multiplier
    }

    // Determine format based on original number
    let format = 'integer'
    if (numStr.includes('.')) {
      format = 'decimal'
    } else if (numStr.includes(',')) {
      format = 'comma'
    }

    return {
      value,
      prefix: prefix || '',
      suffix: (displayMultiplier + suffix) || '',
      animatable: true,
      format,
      hasCommas: numStr.includes(','),
      decimalPlaces: numStr.includes('.') ? (numStr.split('.')[1] || '').length : 0
    }
  }

  /**
   * Format a number according to the original format
   * @param {number} num - The current number value
   * @param {Object} parsed - The parsed format info
   * @returns {string} - Formatted string
   */
  formatValue(num, parsed) {
    if (!parsed.animatable) {
      return parsed.original
    }

    let formatted

    if (parsed.format === 'decimal') {
      formatted = num.toFixed(parsed.decimalPlaces)
    } else if (parsed.hasCommas) {
      formatted = Math.round(num).toLocaleString('en-US')
    } else {
      formatted = Math.round(num).toString()
    }

    return parsed.prefix + formatted + parsed.suffix
  }
}
